import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, RefreshCw, Loader2, Pencil, Check, X, Sparkles, ListPlus, Building2, Link2, CheckCircle2, CloudOff } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { queueVoiceNote, listQueuedNotes, flushVoiceQueue, isNetworkError } from "@/lib/offlineQueue";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchBox } from "@/components/SearchBox";
import { toast } from "sonner";

const pickType = () => ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function Recorder({ onSaved, onQueued, autoStart }) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const chunks = useRef([]);
  const timer = useRef(null);
  const started = useRef(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = pickType();
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); upload(mr.mimeType || type || "audio/webm"); };
      mr.start(1000);
      started.current = Date.now();
      setSecs(0);
      timer.current = setInterval(() => setSecs((Date.now() - started.current) / 1000), 250);
      setRec(mr);
    } catch (e) { toast.error("Microphone access was blocked. Allow it in your browser to record."); }
  };
  const stop = () => { clearInterval(timer.current); rec?.stop(); setRec(null); };
  const upload = async (mime) => {
    const ctype = mime.split(";")[0];
    const blob = new Blob(chunks.current, { type: ctype });
    const duration = (Date.now() - started.current) / 1000;
    setBusy(true);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await api.post("/voice-notes", blob, { headers: { "Content-Type": ctype }, params: { title, duration } });
      toast.success("Voice note saved — transcribing…");
      setTitle("");
      onSaved();
    } catch (e) {
      if (isNetworkError(e)) {
        try {
          await queueVoiceNote({ blob, ctype, title, duration, recorded_at: new Date().toISOString() });
          toast.success("No signal — saved on this device. It will upload automatically when you're back online.");
          setTitle("");
          onQueued();
        } catch { toast.error("Couldn't save the recording on this device."); }
      } else toast.error(apiError(e));
    }
    finally { setBusy(false); }
  };
  useEffect(() => () => clearInterval(timer.current), []);
  const [needTap, setNeedTap] = useState(false);
  useEffect(() => {
    if (!autoStart) return;
    navigator.mediaDevices?.getUserMedia({ audio: true })
      .then((s) => { s.getTracks().forEach((t) => t.stop()); start(); })
      .catch(() => setNeedTap(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  if (needTap && !rec) {
    return (
      <button type="button" data-testid="voice-tap-to-record" onClick={() => { setNeedTap(false); start(); }}
        className="w-full bg-[#18181A] text-white rounded-md py-10 flex flex-col items-center gap-3 active:scale-[0.99] transition-transform">
        <span className="h-16 w-16 rounded-full bg-[#9E382A] flex items-center justify-center"><Mic className="h-7 w-7" /></span>
        <span className="font-medium">Tap to start recording</span>
      </button>
    );
  }

  return (
    <div data-testid="voice-recorder" className="bg-white border border-border rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <Input data-testid="voice-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="sm:max-w-xs" disabled={!!rec || busy} />
      <div className="flex items-center gap-3">
        {rec ? (
          <Button data-testid="voice-stop-btn" onClick={stop} className="bg-[#9E382A] hover:bg-[#9E382A]/90"><Square className="h-4 w-4 mr-1.5" />Stop</Button>
        ) : (
          <Button data-testid="voice-record-btn" onClick={start} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mic className="h-4 w-4 mr-1.5" />}{busy ? "Saving…" : "Record"}</Button>
        )}
        {rec && <span data-testid="voice-timer" className="font-mono text-sm flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#9E382A] animate-pulse" />{mmss(secs)}</span>}
      </div>
    </div>
  );
}

function SummaryBlock({ n, reload, isSuper }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const made = new Set((n.tasks || []).map((t) => t.step_index).filter((i) => i !== null && i !== undefined));
  const mkTask = async (stepIndex) => {
    setBusy(stepIndex ?? "note");
    try {
      const { data } = await api.post(`/voice-notes/${n.id}/task`, stepIndex === undefined ? {} : { step_index: stepIndex });
      toast.success(`Task created: ${data.title}`, { action: { label: "Open Tasks", onClick: () => nav("/tasks") } });
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  const retry = async () => { try { await api.post(`/voice-notes/${n.id}/summarise`); reload(); } catch (e) { toast.error(apiError(e)); } };
  if (n.status !== "done") return null;
  if (n.summary_status === "working" || (!n.summary_status && n.transcript)) {
    return <p data-testid={`voice-summary-working-${n.id}`} className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Writing summary…</p>;
  }
  if (n.summary_status === "failed") {
    return <p className="text-xs text-[#9E382A]">Summary failed. {isSuper && <button type="button" data-testid={`voice-summary-retry-${n.id}`} onClick={retry} className="underline">Try again</button>}</p>;
  }
  return (
    <div className="space-y-2">
      {n.summary && (
        <div data-testid={`voice-summary-${n.id}`} className="bg-[#C6A87C]/10 border-l-2 border-[#C6A87C] rounded-sm px-3 py-2 text-sm flex gap-2">
          <Sparkles className="h-4 w-4 text-[#8a6d3b] shrink-0 mt-0.5" /><span>{n.summary}</span>
        </div>
      )}
      {(n.next_steps || []).length > 0 && (
        <ul className="space-y-1">
          {n.next_steps.map((s, i) => (
            <li key={i} data-testid={`voice-step-${n.id}-${i}`} className="flex items-center gap-2 text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2B4C3F] shrink-0" /><span className="flex-1">{s}</span>
              {made.has(i) ? <span className="text-xs text-[#2B4C3F] flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Task made</span>
                : <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`voice-step-task-${n.id}-${i}`} disabled={busy !== null} onClick={() => mkTask(i)}>
                  {busy === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListPlus className="h-3.5 w-3.5 mr-1" />}Task</Button>}
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`voice-note-task-${n.id}`} disabled={busy !== null} onClick={() => mkTask(undefined)}>
        <ListPlus className="h-3.5 w-3.5 mr-1" />Turn whole note into a task
      </Button>
    </div>
  );
}

function PropertyLink({ n, props, reload, isSuper }) {
  const link = async (pid) => {
    try { await api.put(`/voice-notes/${n.id}/property`, { property_id: pid }); toast.success(pid ? "Linked to property" : "Property unlinked"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const nav = useNavigate();
  if (n.property_id) {
    return (
      <div data-testid={`voice-linked-${n.id}`} className="flex items-center gap-2 text-xs">
        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
        <button type="button" onClick={() => nav(`/properties/${n.property_id}`)} className="font-medium underline underline-offset-2">{n.property_title}</button>
        {isSuper && <button type="button" data-testid={`voice-unlink-${n.id}`} onClick={() => link(null)} className="text-muted-foreground hover:text-foreground">Unlink</button>}
      </div>
    );
  }
  if (!isSuper) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {n.suggested_property_id && (
        <Button size="sm" variant="secondary" className="h-8 text-xs" data-testid={`voice-link-suggested-${n.id}`} onClick={() => link(n.suggested_property_id)}>
          <Link2 className="h-3.5 w-3.5 mr-1" />Link to {n.suggested_property_title}
        </Button>
      )}
      <Select value="" onValueChange={link}>
        <SelectTrigger data-testid={`voice-link-pick-${n.id}`} className="h-8 w-56 text-xs"><Building2 className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Link to a property…" /></SelectTrigger>
        <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function NoteCard({ n, reload, props, isSuper }) {
  const [src, setSrc] = useState(null);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/voice-notes/${n.id}/audio`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [n.id]);
  const save = async () => {
    try { await api.patch(`/voice-notes/${n.id}`, edit); setEdit(null); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { try { await api.delete(`/voice-notes/${n.id}`); toast.success("Voice note deleted"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const retry = async () => { try { await api.post(`/voice-notes/${n.id}/transcribe`); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div data-testid={`voice-note-${n.id}`} className="bg-white border border-border rounded-md p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {edit ? <Input data-testid={`voice-edit-title-${n.id}`} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            : <p className="font-medium truncate">{n.title}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(n.created_at)} · {mmss(n.duration || 0)} · {n.created_by_name}</p>
        </div>
        {isSuper && <div className="flex items-center gap-1 shrink-0">
          {edit ? (<>
            <Button size="icon" variant="ghost" data-testid={`voice-save-${n.id}`} onClick={save}><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setEdit(null)}><X className="h-4 w-4" /></Button>
          </>) : (
            <Button size="icon" variant="ghost" data-testid={`voice-edit-${n.id}`} onClick={() => setEdit({ title: n.title, transcript: n.transcript || "" })}><Pencil className="h-4 w-4" /></Button>
          )}
          <Button size="icon" variant="ghost" data-testid={`voice-delete-${n.id}`} onClick={del}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
        </div>}
      </div>
      {src ? <audio data-testid={`voice-audio-${n.id}`} controls src={src} className="w-full h-10" /> : <div className="h-10 bg-secondary/50 rounded animate-pulse" />}
      {edit ? (
        <Textarea data-testid={`voice-edit-transcript-${n.id}`} rows={5} value={edit.transcript} onChange={(e) => setEdit({ ...edit, transcript: e.target.value })} />
      ) : n.status === "transcribing" ? (
        <p data-testid={`voice-status-${n.id}`} className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Transcribing…</p>
      ) : n.status === "failed" ? (
        <p data-testid={`voice-status-${n.id}`} className="text-sm text-[#9E382A] flex items-center gap-2">Transcription failed.
          {isSuper && <button type="button" data-testid={`voice-retry-${n.id}`} onClick={retry} className="underline inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" />Try again</button>}</p>
      ) : (
        <p data-testid={`voice-transcript-${n.id}`} className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{n.transcript || <span className="text-muted-foreground">(No speech detected)</span>}</p>
      )}
      {!edit && <SummaryBlock n={n} reload={reload} isSuper={isSuper} />}
      {!edit && <PropertyLink n={n} props={props} reload={reload} isSuper={isSuper} />}
    </div>
  );
}

function QueueBanner({ count, onFlush }) {
  const [busy, setBusy] = useState(false);
  if (!count) return null;
  return (
    <div data-testid="voice-queue-banner" className="bg-[#C6A87C]/15 border border-[#C6A87C]/40 rounded-md px-4 py-3 flex items-center gap-3 text-sm">
      <CloudOff className="h-4 w-4 text-[#8a6d3b] shrink-0" />
      <span data-testid="voice-queue-count" className="flex-1">{count} recording{count === 1 ? "" : "s"} saved on this device, waiting to upload.</span>
      <Button size="sm" variant="outline" className="h-8" data-testid="voice-queue-flush" disabled={busy} onClick={async () => { setBusy(true); await onFlush(); setBusy(false); }}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Upload now"}
      </Button>
    </div>
  );
}

export default function VoiceNotes() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isSuper = !!user.is_super_admin;
  const [autoStart] = useState(isSuper && params.get("record") === "1");
  useEffect(() => {
    if (params.get("record")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [props, setProps] = useState([]);
  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)).catch(() => {}); }, []);
  const load = () => api.get("/voice-notes", { params: { q } }).then((r) => setNotes(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [q]);
  const pending = notes.some((n) => n.status === "transcribing" || n.summary_status === "working" || (n.status === "done" && !n.summary_status && n.transcript));
  const [queued, setQueued] = useState(0);
  const refreshQueue = () => listQueuedNotes().then((l) => setQueued(l.length)).catch(() => {});
  const flushNow = async () => {
    const n = await flushVoiceQueue();
    refreshQueue();
    if (!n && !navigator.onLine) toast.error("Still offline — will retry when you're back online.");
  };
  useEffect(() => {
    refreshQueue();
    const onFlushed = () => { refreshQueue(); load(); };
    window.addEventListener("voice-queue-flushed", onFlushed);
    return () => window.removeEventListener("voice-queue-flushed", onFlushed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [pending, q]);
  return (
    <div className="space-y-6" data-testid="voice-notes-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-accent">{isSuper ? "Super-admin" : "Team notes"}</p>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">Voice Notes</h1>
          <p className="text-sm text-muted-foreground mt-1">{isSuper ? "Record a thought, it's saved and written out for you." : "Notes recorded by super-admins. Listen, read and turn next steps into tasks."}</p>
        </div>
        <SearchBox testId="voice-search" placeholder="Search titles and transcripts…" onSearch={setQ} />
      </div>
      {isSuper && <QueueBanner count={queued} onFlush={flushNow} />}
      {isSuper && <Recorder onSaved={load} onQueued={refreshQueue} autoStart={autoStart} />}
      <div className="space-y-3">
        {notes.length === 0 && <p data-testid="voice-empty" className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">{q ? `No voice notes match "${q}".` : isSuper ? "No voice notes yet. Press Record to make your first one." : "No voice notes yet."}</p>}
        {notes.map((n) => <NoteCard key={n.id} n={n} reload={load} props={props} isSuper={isSuper} />)}
      </div>
    </div>
  );
}
